import React, { useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { getDataOnRequest } from '../../redux/actions/CommonActions';
import Swal from 'sweetalert2';
import { redirectUrl } from '../../services/GlobalUrls';

const DocumentExpiryAlert = (props) => {
  const dispatch = useDispatch();
  const lastTriggeredDocKeyRef = useRef('');

  const parseExpiryDate = (dateString) => {
    if (!dateString) return null;
    const trimmed = String(dateString).trim();
    const separator = trimmed.includes('/') ? '/' : trimmed.includes('.') ? '.' : '-';
    const parts = trimmed.split(separator);
    if (parts.length !== 3) return null;
    let year = parts[0];
    let month = parts[1];
    let day = parts[2];
    if (year.length !== 4) {
      day = parts[0];
      month = parts[1];
      year = parts[2];
    }
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    if (Number.isNaN(yearNum) || Number.isNaN(monthNum) || Number.isNaN(dayNum)) return null;
    const date = new Date(yearNum, monthNum - 1, dayNum);
    date.setHours(0, 0, 0, 0);
    return isNaN(date.getTime()) ? null : date;
  };

  const getDaysUntilExpiry = (expiryString) => {
    const expiryDate = parseExpiryDate(expiryString);
    if (!expiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    const diff = expiryDate.getTime() - today.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const isTemporaryCandidate = () => {
    const type = props.userAuth?.job_type || '';
    return (
      type === 'temporaryrequirement' ||
      type === 'temproryrequirement' ||
      type === 'temporary' ||
      type === 'temp'
    );
  };

  const hasDismissedReminder = (documentType) => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(`${documentType}ReminderDismissed`) === 'true';
  };

  const setDismissedReminder = (documentType) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(`${documentType}ReminderDismissed`, 'true');
    }
  };

  const setRTWUpdateInProgress = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('rtwUpdateInProgress', 'true');
    }
  };

  const setDBSUpdateInProgress = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('dbsUpdateInProgress', 'true');
    }
  };

  const setIdentityUpdateInProgress = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('identityUpdateInProgress', 'true');
    }
  };

  const getDocumentWithMaxExpiry = (documents, expiryKey) => {
    const docs = Array.isArray(documents) ? documents : [];
    return docs.reduce((maxDoc, doc) => {
      const expiryValue = doc?.[expiryKey];
      const expiryDate = parseExpiryDate(expiryValue);
      if (!expiryDate) return maxDoc;
      if (!maxDoc) return doc;
      const maxExpiryDate = parseExpiryDate(maxDoc?.[expiryKey]);
      if (!maxExpiryDate) return doc;
      return expiryDate.getTime() > maxExpiryDate.getTime() ? doc : maxDoc;
    }, null);
  };

  const getRightToWorkExpiry = () => {
    const rightToWorks = Array.isArray(props.rightToWorks) ? props.rightToWorks : [];
    const candidates = [...rightToWorks];
    if (props.rightToWork) candidates.push(props.rightToWork);
    const maxDoc = getDocumentWithMaxExpiry(candidates, 'cnd_rightwork_expire_date');
    if (!maxDoc) return null;
    const expiry = maxDoc.cnd_rightwork_expire_date;
    return { expiry, days: getDaysUntilExpiry(expiry) };
  };

  const getIdentityDocumentExpiry = () => {
    const identityDocuments = Array.isArray(props.identityDocuments) ? props.identityDocuments : [];
    const maxDoc = getDocumentWithMaxExpiry(identityDocuments, 'cnd_identity_doc_exp_date');
    if (!maxDoc) return null;
    const expiry = maxDoc.cnd_identity_doc_exp_date;
    return { expiry, days: getDaysUntilExpiry(expiry) };
  };

  const parseDBSCreatedDate = (dateString) => {
    if (!dateString) return null;
    const raw = String(dateString).trim();
    const [datePart, timePart = ''] = raw.split(' ');
    const [day, month, year] = datePart.split('-');
    if (!day || !month || !year) return null;

    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (timePart) {
      const timeMatch = timePart.match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\s*(AM|PM))?$/i);
      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = parseInt(timeMatch[2], 10);
        seconds = parseInt(timeMatch[3], 10);
        const meridiem = timeMatch[4] ? timeMatch[4].toUpperCase() : null;
        if (meridiem === 'AM' && hours === 12) hours = 0;
        if (meridiem === 'PM' && hours < 12) hours += 12;
      }
    }

    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), hours, minutes, seconds);
    return isNaN(date.getTime()) ? null : date;
  };

  const getDBSDocumentExpiry = () => {
    const dbsDocuments = Array.isArray(props.dbsdocuments) ? props.dbsdocuments : [];
    const validDocuments = dbsDocuments.filter(
      (doc) =>
        doc?.cnd_dbs_certificate_status !== 'Deleted' &&
        (doc?.cnd_dbs_type === 'yes' || doc?.cnd_dbs_type === 'Yes') &&
        Boolean(doc?.cnd_dbs_certificate_upload_file) &&
        Boolean(doc?.cnd_dbs_certificate_exp_date)
    );

    if (validDocuments.length === 0) return null;

    const maxExpiryDoc = validDocuments.reduce((maxDoc, doc) => {
      const expiryDate = parseExpiryDate(doc?.cnd_dbs_certificate_exp_date);
      if (!expiryDate) return maxDoc;
      if (!maxDoc) return doc;
      const maxExpiryDate = parseExpiryDate(maxDoc?.cnd_dbs_certificate_exp_date);
      if (!maxExpiryDate) return doc;
      if (expiryDate.getTime() > maxExpiryDate.getTime()) return doc;
      if (expiryDate.getTime() === maxExpiryDate.getTime()) {
        const createdA = parseDBSCreatedDate(doc?.cnd_dbs_certificate_created_date);
        const createdB = parseDBSCreatedDate(maxDoc?.cnd_dbs_certificate_created_date);
        if (createdA && createdB) return createdA.getTime() > createdB.getTime() ? doc : maxDoc;
        if (createdA) return doc;
      }
      return maxDoc;
    }, null);

    if (!maxExpiryDoc) return null;

    const expiry = maxExpiryDoc?.cnd_dbs_certificate_exp_date;
    const days = getDaysUntilExpiry(expiry);
    if (days === null || days > 30) return null;

    return {
      type: 'dbs',
      expiry,
      days,
      daysExpired: Math.abs(days),
      showRemindLater: days >= 1 && days <= 29,
      label: 'DBS Certificate',
      updateUrl: maxExpiryDoc?.cnd_dbs_id ? `/create-dbs-document/${maxExpiryDoc.cnd_dbs_id}` : '/create-dbs-document',
    };
  };

  const getAlertDocuments = () => {
    const docs = [];

    const identityDoc = getIdentityDocumentExpiry();
    if (identityDoc && identityDoc.days !== null && identityDoc.days <= 30) {
      docs.push({
        type: 'identityDoc',
        expiry: identityDoc.expiry,
        days: identityDoc.days,
        showRemindLater: identityDoc.days >= 1 && identityDoc.days <= 29,
        label: 'Identity Document',
        updateUrl: '/identity-document',
      });
    }

    if (isTemporaryCandidate()) {
      const rightToWorkDoc = getRightToWorkExpiry();
      if (rightToWorkDoc && rightToWorkDoc.days !== null && rightToWorkDoc.days <= 30) {
        docs.push({
          type: 'rtw',
          expiry: rightToWorkDoc.expiry,
          days: rightToWorkDoc.days,
          showRemindLater: rightToWorkDoc.days >= 1 && rightToWorkDoc.days <= 29,
          label: 'Right to Work document',
          updateUrl: '/right-to-work',
        });
      }
    }

    const dbsDoc = getDBSDocumentExpiry();
    if (dbsDoc && dbsDoc.days !== null && dbsDoc.days <= 30) {
      docs.push(dbsDoc);
    }

    return docs.sort((a, b) => {
      const aDate = parseExpiryDate(a.expiry);
      const bDate = parseExpiryDate(b.expiry);
      if (aDate && bDate) return bDate.getTime() - aDate.getTime();
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });
  };

  const showExpiryAlertForDocument = (doc, remainingDocs) => {
    if (!doc) return;
    if (hasDismissedReminder(doc.type)) {
      if (remainingDocs.length > 0) {
        showExpiryAlertForDocument(remainingDocs.shift(), remainingDocs);
      }
      return;
    }

    const docKey = `${doc.type}-${doc.expiry}`;
    if (lastTriggeredDocKeyRef.current === docKey) return;

    lastTriggeredDocKeyRef.current = docKey;

    const showRemindLater = Boolean(doc.showRemindLater);

    Swal.fire({
      text: doc.days < 0
        ? `Your ${doc.label} has expired.`
        : `Your ${doc.label} is about to expire.`,
      icon: 'error',
      showCancelButton: showRemindLater,
      confirmButtonText: 'Update Now',
      cancelButtonText: showRemindLater ? 'Remind Me Later' : undefined,
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: 'rtw-expiry-popup',
        title: 'rtw-expiry-title',
        htmlContainer: 'rtw-expiry-html',
        confirmButton: 'rtw-expiry-button',
        cancelButton: 'rtw-expiry-button',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        if (!showRemindLater) {
          if (doc.type === 'rtw') {
            setRTWUpdateInProgress();
          } else if (doc.type === 'dbs') {
            setDBSUpdateInProgress();
          } else if (doc.type === 'identityDoc') {
            setIdentityUpdateInProgress();
          }
        }
        redirectUrl(doc.updateUrl);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        setDismissedReminder(doc.type);
        if (remainingDocs.length > 0) {
          showExpiryAlertForDocument(remainingDocs.shift(), remainingDocs);
        }
      }
    });
  };

  const alertDocumentExpiry = () => {
    const docs = getAlertDocuments();
    if (!docs.length) return;
    showExpiryAlertForDocument(docs.shift(), docs);
  };

  useEffect(() => {
    alertDocumentExpiry();
  }, [props.userAuth?.job_type, props.rightToWork, props.rightToWorks, props.identityDocuments, props.dbsdocuments]);

  useEffect(() => {
    if (props.userAuth?.users_access_token && Array.isArray(props.rightToWorks) && props.rightToWorks.length === 0) {
      dispatch(getDataOnRequest(props.userAuth.users_access_token, 'SetRightToWorks', 'SET_RIGHT_TO_WORKS'));
    }
  }, [dispatch, props.userAuth?.users_access_token, props.rightToWorks]);

  // Clear RTW update flag when RTW data changes (indicating update is complete)
  useEffect(() => {
    if (localStorage.getItem('rtwUpdateInProgress') === 'true') {
      // Check if we're no longer on the update screen and RTW has been updated
      if (window.location.pathname !== '/right-to-work' && 
          !window.location.pathname.includes('create-right-to-work')) {
        localStorage.removeItem('rtwUpdateInProgress');
      }
    }
  }, [props.rightToWork, props.rightToWorks]);

  useEffect(() => {
    if (props.userAuth?.users_access_token && Array.isArray(props.identityDocuments) && props.identityDocuments.length === 0) {
      dispatch(getDataOnRequest(props.userAuth.users_access_token, 'SetIdentityDocuments', 'SET_IDENTITY_DOCUMENTS'));
    }
  }, [dispatch, props.userAuth?.users_access_token, props.identityDocuments]);

  useEffect(() => {
    if (props.userAuth?.users_access_token && Array.isArray(props.dbsdocuments) && props.dbsdocuments.length === 0) {
      dispatch(getDataOnRequest(props.userAuth.users_access_token, 'GetDBSDocuments', 'SET_DBS_DOCUMENT'));
    }
  }, [dispatch, props.userAuth?.users_access_token, props.dbsdocuments]);

  return null;
};

const mapStateToProps = (state) => ({
  userAuth: state.auth.authUserData,
  rightToWork: state.rightToWork.rightToWork,
  rightToWorks: state.rightToWorks.rightToWorks,
  identityDocuments: state.identityDocuments.identityDocuments,
  dbsdocuments: state.dbsdocuments.dbsdocuments,
});

export default connect(mapStateToProps)(DocumentExpiryAlert);
